import type {
  TelephonyEvent, ExtensionState, LiveCallStatus, CallLegDirection, ParkedCallStatus
} from '../contract'

// Internal view-model (camelCase, UI-friendly). Enum VALUES are the contract's snake_case
// wire unions. The store maps snake_case wire events -> these rows and stamps `updated`
// locally (the wire frame carries no per-event timestamp).

export interface ExtensionRow { ext: string; state: ExtensionState; updated: string }
export interface CallRow {
  uniqueId: string; channel: string; extension: string
  callerIdName: string; callerIdNumber: string
  direction: CallLegDirection; status: LiveCallStatus
  connectedLineNumber?: string | null; onHold: boolean; updated: string
}
export interface ParkedRow {
  parkingSpace: string; parkeeChannel: string; callerIdName: string
  callerIdNumber: string; parkerExtension: string; status: ParkedCallStatus
  timeoutSeconds: number; updated: string
}
export interface QueueMemberRow {
  queue: string; memberInterface: string; memberName: string
  paused: boolean; loggedIn: boolean; status: string; updated: string
}
export interface QueueStatsRow {
  callsWaiting: number; longestWaitSeconds: number; callsAnswered: number
  avgAnswerSeconds: number; serviceLevel: number
}
export interface AgentStatsRow {
  queue: string; agent: string; callsAnswered: number
  avgRingSeconds: number; avgCallSeconds: number; updated: string
}
export interface QueueSummary {
  queue: string; agents: number; paused: number; available: number
  stats?: QueueStatsRow
}
export interface VoicemailRow { mailbox: string; newMessages: number; oldMessages: number; updated: string }

export interface TelephonySnapshot {
  extensions: ExtensionRow[]
  calls: CallRow[]
  parked: ParkedRow[]
  queueMembers: QueueMemberRow[]
  queues: QueueSummary[]
  voicemail: VoicemailRow[]
  agentStats: AgentStatsRow[]
}

const EMPTY: TelephonySnapshot = {
  extensions: [], calls: [], parked: [], queueMembers: [], queues: [], voicemail: [], agentStats: []
}

export class TelephonyStore {
  private extensions = new Map<string, ExtensionRow>()
  private calls = new Map<string, CallRow>()
  private parked = new Map<string, ParkedRow>()
  private queueMembers = new Map<string, QueueMemberRow>()
  private queueStats = new Map<string, QueueStatsRow>()
  private agentStats = new Map<string, AgentStatsRow>()
  private voicemail = new Map<string, VoicemailRow>()

  private snapshot: TelephonySnapshot = EMPTY
  private listeners = new Set<() => void>()

  getSnapshot = (): TelephonySnapshot => this.snapshot
  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb); return () => this.listeners.delete(cb)
  }

  apply(e: TelephonyEvent): void {
    const now = new Date().toISOString()
    switch (e.type) {
      case 'extension_state':
        this.extensions.set(e.extension, { ext: e.extension, state: e.state, updated: now })
        break
      case 'call_info': {
        if (e.status === 'ended') this.calls.delete(e.unique_id)
        else {
          const prev = this.calls.get(e.unique_id)
          this.calls.set(e.unique_id, {
            uniqueId: e.unique_id, channel: e.channel, extension: e.extension,
            callerIdName: e.caller_id_name, callerIdNumber: e.caller_id_number,
            direction: e.direction, status: e.status, connectedLineNumber: e.connected_line_number,
            onHold: e.status === 'on_hold' ? true : (prev?.onHold ?? false), updated: now
          })
        }
        break
      }
      case 'call_hold': {
        const c = this.calls.get(e.unique_id)
        if (c) this.calls.set(e.unique_id, {
          ...c, onHold: e.on_hold, status: e.on_hold ? 'on_hold' : 'connected', updated: now
        })
        break
      }
      case 'parked_call':
        if (e.status === 'retrieved' || e.status === 'abandoned' || e.status === 'timed_out') {
          this.parked.delete(e.parking_space)
        } else {
          this.parked.set(e.parking_space, {
            parkingSpace: e.parking_space, parkeeChannel: e.parkee_channel, callerIdName: e.caller_id_name,
            callerIdNumber: e.caller_id_number, parkerExtension: e.parker_extension, status: e.status,
            timeoutSeconds: e.timeout_seconds, updated: now
          })
        }
        break
      case 'queue_member':
        this.queueMembers.set(`${e.queue}/${e.member_interface}`, {
          queue: e.queue, memberInterface: e.member_interface, memberName: e.member_name,
          paused: e.paused, loggedIn: e.logged_in, status: e.status, updated: now
        })
        break
      case 'queue_stats':
        this.queueStats.set(e.queue, {
          callsWaiting: e.calls_waiting, longestWaitSeconds: e.longest_wait_seconds,
          callsAnswered: e.calls_answered, avgAnswerSeconds: e.avg_answer_seconds,
          serviceLevel: e.service_level
        })
        break
      case 'voicemail_count':
        this.voicemail.set(e.mailbox, {
          mailbox: e.mailbox, newMessages: e.new_messages, oldMessages: e.old_messages, updated: now
        })
        break
      case 'agent_stats':
        this.agentStats.set(`${e.queue}/${e.agent}`, {
          queue: e.queue, agent: e.agent, callsAnswered: e.calls_answered,
          avgRingSeconds: e.avg_ring_seconds, avgCallSeconds: e.avg_call_seconds, updated: now
        })
        break
    }
    this.rebuild()
  }

  private rebuild(): void {
    const queueMembers = [...this.queueMembers.values()]
    const byQueue = new Map<string, QueueSummary>()
    const ensure = (q: string): QueueSummary => {
      let s = byQueue.get(q)
      if (!s) { s = { queue: q, agents: 0, paused: 0, available: 0 }; byQueue.set(q, s) }
      return s
    }
    for (const m of queueMembers) {
      if (!m.loggedIn) continue
      const q = ensure(m.queue)
      q.agents++
      if (m.paused) q.paused++
      else if (m.status === 'not_in_use') q.available++
    }
    for (const [queue, stats] of this.queueStats) ensure(queue).stats = stats
    this.snapshot = {
      extensions: [...this.extensions.values()].sort((a, b) => a.ext.localeCompare(b.ext)),
      calls: [...this.calls.values()].sort((a, b) => b.updated.localeCompare(a.updated)),
      parked: [...this.parked.values()].sort((a, b) => a.parkingSpace.localeCompare(b.parkingSpace)),
      queueMembers,
      queues: [...byQueue.values()].sort((a, b) => a.queue.localeCompare(b.queue)),
      voicemail: [...this.voicemail.values()].sort((a, b) => a.mailbox.localeCompare(b.mailbox)),
      agentStats: [...this.agentStats.values()].sort((a, b) => a.agent.localeCompare(b.agent))
    }
    for (const l of this.listeners) l()
  }
}
